// Dev-only DISPATCHER for epics (plans) — `GET /v1/plans` (the index), `GET
// /v1/plans/:planId` (the epic view) and `POST /v1/plans/:planId/cancel`
// (#1906's stop control on it).
//
// Split out of `mocks.ts` (plan 101 slice 15 step 4). `null` means "not mine"
// and `mockFetch` falls through.
//
// The whole family was absent until #1981, which is why `/fleet/plans/:plan_id`
// could only ever render its error boundary under mocks. Same
// id-encodes-the-case convention as the run stories: `plan_mock_done` has every
// slice merged (nothing to stop → control ABSENT), any other id has unfinished
// slices (control PRESENT).
//
// ## THE TWO ENDPOINTS ARE ONE SOURCE, AND THIS FILE IS THE DERIVATION
//
// Every plan is described exactly once, as a `PlanStory` in
// `plan-stories-mocks.ts`. `GET /v1/plans/:id` serves that story's groups and
// `GET /v1/plans` serves {@link summarise} OF THE SAME GROUPS, so the two
// endpoints cannot disagree — there is nothing to keep in step.
//
// THE MINT LANE IS DERIVED HERE TOO, never declared beside a story:
// `planSourceOf` off the id each body carries — the same function the port's
// two reads classify with — so neither endpoint, nor the any-id fallback below,
// can serve a lane the server would not derive for that id. The stories
// declared it until #4404's review caught the drift: a `scout` badge under an
// id whose mint prefix says `custom`.
//
// It was two hand-maintained descriptions until CodeRabbit found one of the
// disagreements on PR #3780, and a sweep of all four plans found TEN across
// eight fields: `plan_mock_done` and `plan_mock_multi` named a `raw_task_id` the
// detail answered `null` for; `plan_mock01`'s index headline named a slice its
// own detail did not contain; and `plan_mock_stalled` disagreed with itself on
// status, all three counts, the lead title and the repo — its index row promised
// two slices, one stalled, in `docs-site`, while its detail served three
// unstalled ones in `api-gateway`. A mock that contradicts itself teaches the
// next reader that `/v1/plans` and `/v1/plans/:id` legitimately differ, and
// hides a real inconsistency behind an expected one.
//
// The data lives next door and the derivation lives here on purpose: the stories
// say what each plan IS, and this file is the part worth reading — how one story
// becomes two bodies. Splitting them is also what keeps each under the 300-line
// mock ceiling (`mock-size.test.ts`), which the consolidation had broken.

import {
  isPlanFilesGroupId,
  PLAN_LIST_DEFAULT_PER_PAGE,
  PLAN_LIST_MAX_PER_PAGE,
  PlanBucketSchema,
  PlanGroupStatus,
  type PlanListCounts,
  type PlanListPage,
  type PlanProjection,
  type PlanProjectionGroup,
  type PlanSummary,
  planBucket,
  planSourceOf,
  planStopReason,
} from '@developerz/domain';
import { legReads, storyGoalState } from './plan-leg-mocks';
import { type PlanStory, STORIES } from './plan-stories-mocks';
import { BULK_PLAN_STORIES } from './plan-story-bulk-mocks';
import { CUSTOM_PLAN_STORY } from './plan-story-custom-mocks';
import { GOAL_PLAN_STORY } from './plan-story-goal-mocks';
import { PLANFILES_LEAD_STORY, PLANFILES_ONLY_STORY } from './plan-story-planfiles-mocks';

/**
 * Every story `/v1/plans` and `/v1/plans/:id` can serve — `plan-stories-mocks.ts`'s
 * corpus plus #4341's `initiatedBy: 'user'` fixture (`plan-story-custom-mocks.ts`), #5136's
 * bulk corpus (`plan-story-bulk-mocks.ts`, 105 rows: a second `per_page=100` page in dev)
 * and slice 12's multi-repo goal (`plan-story-goal-mocks.ts`). One array, so the index and
 * detail responders below mirror each other whichever module a story lives in.
 */
const ALL_STORIES: readonly PlanStory[] = [
  ...STORIES,
  CUSTOM_PLAN_STORY,
  GOAL_PLAN_STORY,
  ...BULK_PLAN_STORIES,
  // #5189 — the plan-files headline-skip pair: see `plan-story-planfiles-mocks.ts`.
  PLANFILES_LEAD_STORY,
  PLANFILES_ONLY_STORY,
];

/**
 * The index row, TALLIED THE WAY THE SERVER TALLIES IT. `listPlansByAccount`
 * classifies each group with `planGroupDisposition`, takes the lowest-`position`
 * slice as the lead (SKIPPING #5172's `plan-files` bookkeeping group, #5189 —
 * see below), and aggregates `repo_full_name` distinct and sorted; this does
 * the same over the same groups, so the fixture cannot drift from the port any
 * more than it can drift from its own detail endpoint. `source` is
 * `planSourceOf` off the story's own id — the same derivation the port applies
 * to the row.
 */
function summarise(story: PlanStory): PlanSummary {
  const groups = legReads(story.groups); // the SAME leg read `projection` serves (slice 12)
  const ordered = [...groups].sort((a, b) => a.position - b.position);
  if (ordered[0] === undefined) throw new Error(`plan story ${story.planId} has no groups`);
  // #5189 — the SAME rule `selectPlanHeadlines` (`@developerz/db`) applies in
  // SQL: the headline never names the plan-files bookkeeping group unless it
  // is the plan's ONLY group. `groupCount`/`mergedCount`/etc below still tally
  // every group, plan-files included — only the headline pick is filtered.
  const nonPlanFiles: PlanProjectionGroup[] = ordered.filter((g) => !isPlanFilesGroupId(g.groupId));
  const lead = nonPlanFiles[0] ?? ordered[0];
  return {
    planId: story.planId,
    status: story.status,
    rawTaskId: story.rawTaskId,
    initiatedBy: story.initiatedBy,
    owner: story.owner,
    source: planSourceOf(story.planId),
    repoFullName: lead.repoFullName,
    repoFullNames: [...new Set(ordered.map((g) => g.repoFullName))].sort(),
    leadTitle: lead.title,
    groupCount: ordered.length,
    mergedCount: ordered.filter((g) => g.disposition === 'landed').length,
    noWorkCount: ordered.filter((g) => g.disposition === 'no_work').length,
    stalledCount: ordered.filter((g) => g.disposition === 'stalled').length,
    // #5135 — the SAME row set the other counts tally, never a second pass over
    // a copy: a group's own `status` column, exactly what `tallyPlanDispositions`
    // (`@developerz/db`) reads for the real port. No story currently mints a
    // `held` group, so this is 0 today — the day one does, this line is already
    // the truth for it.
    heldCount: ordered.filter((g) => g.status === PlanGroupStatus.Held).length,
    // DERIVED, never restated (#5102): the mock calls the same `planStopReason`
    // the port calls, over the story's own groups, so a fixture cannot answer
    // a stop reason the server would not derive for the same slices.
    stopReason: planStopReason({
      planStatus: story.status,
      groups: ordered.map((g) => ({
        disposition: g.disposition,
        stallReason: g.stallReason,
        heldReason: g.heldOn?.reason ?? null,
      })),
    }),
    goalState: storyGoalState(story.status, groups),
    createdIso: story.createdIso,
    updatedIso: story.updatedIso,
  };
}

const plans: PlanSummary[] = ALL_STORIES.map(summarise);

/**
 * The tab counts a list page carries beside its rows (#5135) — tallied over the
 * SAME set the page's `total` divides, with {@link planBucket}, the one pure
 * function both the server and (later, #5136) the dashboard partition a plan
 * with. Never a hand-kept second count: `sum(buckets) === all` by construction.
 */
function bucketCounts(rows: readonly PlanSummary[]): PlanListCounts {
  const counts: PlanListCounts = {
    all: rows.length,
    closed: 0,
    completed: 0,
    stalled: 0,
    awaiting_approval: 0,
    in_progress: 0,
  };
  for (const p of rows) counts[planBucket(p)] += 1;
  return counts;
}

/**
 * Real port defaults (`listPlansByAccount`, `@developerz/db`) — this plane
 * mirrors the clamp, not the client's ask, against the SAME
 * `@developerz/domain` constants the port clamps with, so a bound change
 * cannot drift between the two.
 */
function clampPlanListLimit(perPage: string | null): number {
  const asked = perPage === null ? Number.NaN : Number(perPage);
  if (!Number.isFinite(asked)) return PLAN_LIST_DEFAULT_PER_PAGE;
  return Math.min(PLAN_LIST_MAX_PER_PAGE, Math.max(1, Math.trunc(asked)));
}

function projection(planId: string): PlanProjection {
  // Any id that names no case is the live story, under the id that was asked
  // for — the same convention the run fixtures use.
  const story = ALL_STORIES.find((p) => p.planId === planId) ?? ALL_STORIES[0];
  if (story === undefined) throw new Error('no plan stories');
  const groups = legReads(story.groups);
  // The `plans` ROW's own three fields (arc 101 doc 08) — this body IS the wire
  // shape the dashboard types against, and all three come from the SAME story
  // the index row above was summarised from. `source` is not one of them: it is
  // DERIVED off the id this body carries (`planSourceOf` — the server's own
  // rule), so the fallback answers the lane the server would derive for the
  // ASKED id, never a lane inherited from whichever story answered.
  return {
    planId,
    status: story.status,
    rawTaskId: story.rawTaskId,
    initiatedBy: story.initiatedBy,
    owner: story.owner,
    llm: null,
    source: planSourceOf(planId),
    goalState: storyGoalState(story.status, groups),
    groups,
  };
}

/** Apply `repo` and `q` over `rows` — narrowing before `bucket` (#5136, see {@link plansIndexPage}). */
function applyRepoAndQuery(
  rows: readonly PlanSummary[],
  params: URLSearchParams,
): readonly PlanSummary[] {
  const repo = params.get('repo');
  const afterRepo = repo === null ? rows : rows.filter((p) => p.repoFullNames.includes(repo));
  const q = params.get('q');
  if (q === null) return afterRepo;
  const qLower = q.toLowerCase();
  return afterRepo.filter((p) => {
    const title = p.leadTitle.toLowerCase();
    const id = p.planId.toLowerCase();
    return title.includes(qLower) || id.includes(qLower);
  });
}

/**
 * `GET /v1/plans` — keyed `plans`, NOT `{data, next_cursor}`: the route pages
 * NUMBERED (`page`/`per_page`/`total`), not by cursor, so a `next_cursor`
 * envelope is the wrong SHAPE for it regardless of how many pages exist —
 * numbered paging is answered by `page`/`total`, and a cursor field has
 * nothing of its own to point at. (Not because a second page can't exist:
 * #5136's `BULK_PLAN_STORIES` below gives this pager a real page two, on
 * purpose, for `per_page=100` to have something to page through in dev.)
 *
 * `?initiated_by=` (#4340) is read the same way the real route reads it —
 * filtering the SUMMARY rows the SAME `initiatedBy` `summarise()` derived
 * them with, so the mock plane can serve the Custom plans section (#4341)
 * without a second, independently-filtered list to drift from this one. Any
 * other/absent value serves every plan, matching the route's own "omitted,
 * never defaulted" stance on an absent filter.
 *
 * Order matches the route's own docblock (`apps/api/src/routes/plans.ts`)
 * and `PlanListPageSchema`'s field order: `initiated_by` first, then `repo`
 * + `q`, THEN `bucket`. `counts` is tallied over the set after `repo`/`q`
 * are applied but BEFORE `bucket` is applied, so a tab's count stays
 * consistent regardless of which tab the caller picked.
 */
function plansIndexPage(path: string): Promise<unknown> {
  const params = new URLSearchParams(path.split('?')[1] ?? '');
  const initiatedBy = params.get('initiated_by');
  const initiatedFiltered =
    initiatedBy === null ? plans : plans.filter((p) => p.initiatedBy === initiatedBy);
  const narrowed = applyRepoAndQuery(initiatedFiltered, params);
  const counts = bucketCounts(narrowed);
  // `PlanBucketSchema.safeParse` rejects an unknown value; the route would
  // 400 the same input, so we silently OMIT a bogus filter (mirror of the
  // route's own "absent, never defaulted" stance).
  const bucketParsed = PlanBucketSchema.safeParse(params.get('bucket'));
  const bucketed =
    bucketParsed.success && bucketParsed.data !== undefined
      ? narrowed.filter((p) => planBucket(p) === bucketParsed.data)
      : narrowed;
  const sorted = [...bucketed].sort((a, b) => b.updatedIso.localeCompare(a.updatedIso));
  const perPage = clampPlanListLimit(params.get('per_page'));
  const page = Math.max(1, Math.trunc(Number(params.get('page')) || 1));
  const envelope: PlanListPage = {
    plans: sorted.slice((page - 1) * perPage, page * perPage),
    page,
    per_page: perPage,
    total: sorted.length,
    counts,
    // The repo VOCABULARY is the stage-1 set — tenant + `initiated_by` only,
    // BEFORE `repo`/`q` narrow it (`listing.ts`'s own `repos` derivation,
    // `PlanListPageSchema` docblock) — never the narrowed set `counts`/`total`
    // divide. Built off `initiatedFiltered`, not `narrowed`: a `?repo=` pick
    // must never shrink the dropdown to the one repo already chosen.
    repos: [...new Set(initiatedFiltered.flatMap((p) => p.repoFullNames))].sort(),
  };
  return Promise.resolve(envelope);
}

/** Serve the epic family; `null` for anything else. */
export function plansMock(path: string, method: string): Promise<unknown> | null {
  const bare = path.split('?')[0] ?? path;
  if (bare === '/v1/plans') {
    if (method === 'GET') return plansIndexPage(path);
    // The scout entry (`plan.start`), which this plane did not serve at all —
    // the family's probe is the GET, so `<StartPlanDialog/>`'s submit fell
    // through to `Unhandled mock route` and the dialog could never be driven
    // past its form. A 202 names a TASK, never a run: the box mints the run id
    // at claim time, and `readStartOutcome` reads `task_id` or reports `unclear`.
    if (method === 'POST') return Promise.resolve({ task_id: 'tsk_mock_scout' });
    return null;
  }
  if (!bare.startsWith('/v1/plans/')) return null;
  const rest = bare.slice('/v1/plans/'.length);
  if (rest.endsWith('/cancel') && method === 'POST') {
    // The THREE lists, not a count: a `pr_review` slice cannot be interrupted,
    // so the story that matters is the one where a stop half-succeeds and says so.
    return Promise.resolve({
      plan_id: decodeURIComponent(rest.slice(0, -'/cancel'.length)),
      cancelled_task_ids: ['tsk_slice_2'],
      skipped_review_task_ids: ['tsk_slice_3'],
      already_terminal_task_ids: ['tsk_slice_1'],
    });
  }
  if (method !== 'GET' || rest.includes('/')) return null;
  return Promise.resolve(projection(decodeURIComponent(rest)));
}
